import { type JSX, useState } from 'react';

import { getWindowSafe } from '@change-corgi/core/window';
import { Tab, TabGroup, TabPanel, Tabs } from '@change-corgi/design-system-legacy/components/navigation';
import { Box, Container, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import { useStarterDashboardV2TabsStateContext } from 'src/app/pages/starterDashboardV2/pageContext';
import {
	DM_TAB_CLICK,
	HOME_TAB_CLICK,
	SHARE_TAB_CLICK,
	SUPPORTERS_TAB_CLICK,
} from 'src/app/pages/starterDashboardV2/shared/constants';

import { useDashboardReskinExperiment } from '../../../../../../hooks/useDashboardReskinExperiment';
import { usePageBody } from '../../../../hooks/usePageBody';
import { DecisionMakersList } from '../../../DecisionMakersList';
import { RecentSupporters } from '../../../EngageSupporters/components/RecentSupporters';
import { GrowPetition } from '../../../GrowPetition';
import type { WindowWithOptimizely } from '../../../GrowPetition/components/ShareYourPetition';
import { NotificationsCenter } from '../../../NotificationsCenter';
import { PageBanner } from '../../../PageBanner';
import { PetitionInsights } from '../../../PetitionInsights';
import { PetitionOverview } from '../../../PetitionOverview';
import { Recommendations } from '../../../Recommendations';
import { StarterDashboardComments } from '../../../StarterDashboardComments';
import { StepsToVictory } from '../../../StepsToVictory';
import { UpdateSupporters } from '../../../UpdateSupporters';
import { useDashboardTabs } from '../../hooks/useDashboardTabs';

import { DesktopTabs as DesktopTabsReskin } from './index_new';

const TabContainerTitle = ({ title }: { title: string }) => (
	<Heading as="h3" size="h3">
		{title}
	</Heading>
);

const SupportersComponent = () => {
	return (
		<Container px={10}>
			<Flex sx={{ flexDirection: 'column', gap: 16 }}>
				<TabContainerTitle title="Message your supporters" />
				<UpdateSupporters />
			</Flex>
		</Container>
	);
};

const DmComponent = () => {
	return (
		<Container px={10}>
			<Flex sx={{ flexDirection: 'column', gap: 16 }}>
				<TabContainerTitle title="Connect decision makers" />
				<DecisionMakersList />
			</Flex>
		</Container>
	);
};

const ShareComponent = () => {
	return (
		<Container px={10}>
			<Flex sx={{ flexDirection: 'column', gap: 16 }}>
				<TabContainerTitle title="Share your petition" />
				<Recommendations viewContext="share" />
				<GrowPetition />
				<StepsToVictory />
			</Flex>
		</Container>
	);
};

const HomeComponent = () => {
	const [commentsVisibility, setCommentsVisibility] = useState(true);

	return (
		<>
			<PageBanner />
			<Container px={10}>
				<Flex sx={{ flexDirection: 'column', gap: 16 }}>
					<NotificationsCenter />
					<TabContainerTitle title="Home" />
					<PetitionOverview />
					<Recommendations viewContext="home" />
					<RecentSupporters />
					{commentsVisibility && <StarterDashboardComments setCommentsVisibility={setCommentsVisibility} />}
					<PetitionInsights />
				</Flex>
			</Container>
		</>
	);
};

export function DesktopTabs(): JSX.Element {
	const { data } = usePageBody();
	const { actions } = useDashboardTabs();
	const windowObject = getWindowSafe() as WindowWithOptimizely;
	const {
		data: { selectedTab },
		actions: { changeSelectedTab },
	} = useStarterDashboardV2TabsStateContext();

	const isSelected = (tab: string) => selectedTab === tab;
	const switchTab = (event: string, tab: string) => {
		if (windowObject && windowObject.optimizely) {
			windowObject.optimizely.push({
				type: 'activate',
			});
		}
		changeSelectedTab(tab, (prev) => {
			actions.trackClick(event, prev);
		});
	};

	const {
		data: { isEnabled },
	} = useDashboardReskinExperiment();

	if (isEnabled) {
		return <DesktopTabsReskin />;
	}

	const boxTitle = (event: string, tab: string, title: string) => (
		<Box tabIndex={isSelected(tab) ? -1 : 0} onKeyDown={(e) => e.key === 'Enter' && switchTab(event, tab)}>
			<Text as="p" size="large" fontWeight={isSelected(tab) ? 'bold' : 'normal'}>
				{title}
			</Text>
		</Box>
	);

	return (
		<Tabs selected={selectedTab} data-testid="desktop-tabs-control">
			<TabGroup sticky containerVariant="default" stickyPos={data.bannerHeight}>
				<Tab data-testid="home-tab" name="home" onSelect={() => switchTab(HOME_TAB_CLICK, 'home')}>
					{boxTitle(HOME_TAB_CLICK, 'home', 'Home')}
				</Tab>
				<Tab data-testid="share-tab" name="share" onSelect={() => switchTab(SHARE_TAB_CLICK, 'share')}>
					<Flex
						sx={{ pl: 8, display: 'flex', gap: 8, position: 'relative' }}
						tabIndex={isSelected('share') ? -1 : 0}
						onKeyDown={(e) => e.key === 'Enter' && switchTab(SHARE_TAB_CLICK, 'share')}
					>
						{boxTitle(SHARE_TAB_CLICK, 'share', 'Share your petition')}
					</Flex>
				</Tab>
				<Tab data-testid="dms-tab" name="dm" onSelect={() => switchTab(DM_TAB_CLICK, 'dm')}>
					{boxTitle(DM_TAB_CLICK, 'dm', 'Connect to decision makers')}
				</Tab>
				<Tab
					data-testid="supporters-tab"
					name="supporters"
					onSelect={() => switchTab(SUPPORTERS_TAB_CLICK, 'supporters')}
				>
					{boxTitle(SUPPORTERS_TAB_CLICK, 'supporters', 'Ask supporters for help')}
				</Tab>
			</TabGroup>
			<TabPanel name="home">
				<Box backgroundColor="primary-greyBackground" py={16}>
					{isSelected('home') && <HomeComponent />}
				</Box>
			</TabPanel>
			<TabPanel name="share">
				<Box backgroundColor="primary-greyBackground" py={16}>
					{isSelected('share') && <ShareComponent />}
				</Box>
			</TabPanel>
			<TabPanel name="dm">
				<Box backgroundColor="primary-greyBackground" py={16}>
					{isSelected('dm') && <DmComponent />}
				</Box>
			</TabPanel>
			<TabPanel name="supporters">
				<Box backgroundColor="primary-greyBackground" py={16}>
					{isSelected('supporters') && <SupportersComponent />}
				</Box>
			</TabPanel>
		</Tabs>
	);
}
